"use client";

import React, { useEffect, useMemo, useRef, useState } from "react";
import type { ReactElement } from "react";
import type { ReportCheckoutButtonProps } from "../../paid-reports";
import { PROBLEMS, type ProblemDefinition } from "./problems";
import { PLAN_DAYS, PROBLEM_FINDER_PLAN_PRICE_USD } from "./plan-types";
import { ProblemFinderAnalysisResponseSchema, type ProblemFinderAnalysisResponse } from "./types";

// Props provided by StepByStepAssessment, same contract as the Life Rebuild result page:
// the app injects the checkout button, spinner and icons so this stays free of Stripe,
// next and lucide.
export interface ProblemFinderResultPageProps {
  runId: string;
  publicAccessToken: string;
  email?: string;
  assessmentType?: string;
  isComplete: boolean;
  analysisResult: unknown;
  posthog?: {
    capture: (event: string, properties?: Record<string, unknown>) => void;
  };
  // Optional in the shared ResultPage contract; StepByStepAssessment always passes it.
  components?: {
    ReportCheckoutButton: React.ComponentType<ReportCheckoutButtonProps>;
    LoadingSpinner?: React.ComponentType;
    icons: {
      CheckCircle: React.ComponentType<{ className?: string }>;
    };
  };
}

const BRAND = "#2A6354";
const BUY_BUTTON_CLASS =
  "w-full min-h-[52px] rounded-lg px-6 text-lg font-bold text-white bg-[#2A6354] hover:bg-[#1f4a3f] transition-colors shadow-lg disabled:opacity-60";

function planFaq(problem: ProblemDefinition) {
  return [
    {
      question: "Is this a subscription?",
      answer: `No. One payment of $${PROBLEM_FINDER_PLAN_PRICE_USD}. Nothing renews.`,
    },
    {
      question: "How is it different from my free result?",
      answer: `Your result names the problem and gives you step one. Your ${problem.planName} gives you all ${PLAN_DAYS} steps with how to do each one, a tool made for your problem, two checkpoints, and three ways through it to test.`,
    },
    {
      question: "What if it doesn't help?",
      answer: "Email support@lifehack.org within 30 days for a full refund.",
    },
  ];
}

const ALSO_INSIDE = [
  "How to do every step, plus a 5-minute version for bad days",
  "A ready-to-use tool for your problem, filled in from your answers",
  "Three ways through this that fit your answers, with a small test for each",
  "What has kept this stuck before, and how the plan gets around it",
  "Day 7 and day 14 checkpoints, so you can tell it's working",
  "What to do in the week it falls apart",
];

function CheckMark() {
  return (
    <svg className="w-5 h-5 text-[#2A6354] flex-shrink-0 mt-0.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}

function LockIcon() {
  return (
    <svg className="w-4 h-4 text-gray-500 ml-auto flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect width="18" height="11" x="3" y="11" rx="2" ry="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  );
}

function PlanOffer({
  runId,
  email,
  problem,
  analysis,
  ReportCheckoutButton,
}: {
  runId: string;
  email?: string;
  problem: ProblemDefinition;
  analysis: ProblemFinderAnalysisResponse;
  ReportCheckoutButton: React.ComponentType<ReportCheckoutButtonProps>;
}) {
  const days = Array.from({ length: PLAN_DAYS }, (_, index) => index + 1);

  return (
    <>
      <section
        id="problem-plan"
        aria-labelledby="problem-plan-heading"
        className="bg-white rounded-2xl border-2 border-[#2A6354] shadow-lg p-6 md:p-8 space-y-7"
      >
        <div className="space-y-2">
          <p className="text-sm uppercase tracking-wide text-[#2A6354] font-semibold">Your {problem.planName}</p>
          <h2 id="problem-plan-heading" className="text-2xl md:text-3xl font-bold text-gray-900 leading-tight">
            {problem.offerHeadline}
          </h2>
          <p className="text-base text-gray-600">
            Written from your answers. One step a day, sized to the time you said you have.
          </p>
        </div>

        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-gray-900">Your first {PLAN_DAYS} days</h3>
          <div className="grid grid-cols-7 gap-1.5" aria-hidden="true">
            {days.map((day) => (
              <div
                key={day}
                className={
                  day === 1
                    ? "h-11 rounded-md bg-[#2A6354] text-white flex items-center justify-center text-sm font-bold"
                    : day === 7 || day === 14
                      ? "h-11 rounded-md bg-amber-100 text-amber-900 flex items-center justify-center text-sm font-bold"
                      : "h-11 rounded-md bg-gray-100 text-gray-600 flex items-center justify-center text-sm font-semibold"
                }
              >
                {day}
              </div>
            ))}
          </div>
          <ul className="space-y-2">
            <li className="flex gap-3 items-baseline text-sm text-gray-900">
              <span className="font-bold text-[#2A6354] flex-shrink-0">Day 1</span>
              <span>{analysis.thisWeek.step}</span>
            </li>
            {[2, 3].map((day) => (
              <li key={day} className="flex gap-3 items-center text-sm">
                <span className="font-bold text-gray-600 flex-shrink-0">Day {day}</span>
                <span className="text-gray-400 select-none blur-[4px]" aria-hidden="true">
                  Unlocked in your plan
                </span>
                <LockIcon />
              </li>
            ))}
          </ul>
          <p className="text-sm text-gray-600">Days 7 and 14 are checkpoints, so you can tell it&apos;s working.</p>
        </div>

        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-gray-900">Also inside</h3>
          <ul className="space-y-2.5">
            {ALSO_INSIDE.map((item) => (
              <li key={item} className="flex items-start gap-2.5 text-sm text-gray-800">
                <CheckMark />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="border-t border-gray-200 pt-6 flex flex-col items-center gap-3 text-center">
          <p className="flex items-baseline gap-2">
            <span className="text-4xl font-bold text-gray-900">${PROBLEM_FINDER_PLAN_PRICE_USD}</span>
            <span className="text-base text-gray-600">one time, yours to keep</span>
          </p>
          <div className="w-full sm:max-w-sm">
            <ReportCheckoutButton
              runId={runId}
              email={email}
              assessmentType="problem-finder"
              ctaLocation="inline_offer"
              className={BUY_BUTTON_CLASS}
            >
              Get My {problem.planName.replace(/^14-Day /, "")}
            </ReportCheckoutButton>
          </div>
          <p className="text-sm text-gray-700">30-day money-back guarantee</p>
          <p className="text-xs text-gray-600">
            No subscription. Secure checkout opens right here, and your plan arrives on screen and by email.
          </p>
        </div>
      </section>

      <section aria-label="Questions about the plan" className="bg-white rounded-2xl border border-gray-200 px-6 py-2">
        {planFaq(problem).map((item, index, all) => (
          <div key={item.question} className={index < all.length - 1 ? "py-4 border-b border-gray-200" : "py-4"}>
            <h3 className="text-base font-semibold text-gray-900">{item.question}</h3>
            <p className="text-sm text-gray-600 mt-1">{item.answer}</p>
          </div>
        ))}
      </section>
    </>
  );
}

// The analysis takes about half a minute. One static line for that long reads as stuck,
// so the wait says what is happening and how long it takes. It stops on the last step.
const WAITING_STEPS = [
  "Reading your answers...",
  "Naming the problem underneath your answers...",
  "Working out why it keeps coming back...",
  "Sizing a first step to the time you have...",
];
const WAITING_STEP_MS = 7000;

function WaitingForResult(): ReactElement {
  const [step, setStep] = useState(0);
  useEffect(() => {
    if (step >= WAITING_STEPS.length - 1) return;
    const timer = setTimeout(() => setStep((current) => current + 1), WAITING_STEP_MS);
    return () => clearTimeout(timer);
  }, [step]);

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-6">
      <div className="text-center max-w-md" role="status" aria-live="polite">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto mb-4" />
        <p className="text-gray-800 text-lg">{WAITING_STEPS[step]}</p>
        <p className="text-sm text-gray-500 mt-2">This takes about half a minute. Your result appears here.</p>
      </div>
    </div>
  );
}

export function ProblemFinderResultPage({
  runId,
  email,
  assessmentType,
  isComplete,
  analysisResult,
  posthog,
  components,
}: ProblemFinderResultPageProps): ReactElement {
  if (!components) {
    throw new Error("ProblemFinderResultPage needs the app-provided components");
  }
  const { LoadingSpinner, ReportCheckoutButton } = components;
  const hasTrackedResultView = useRef(false);
  // Parsed once per result: a fresh object each render would rebuild the observer below.
  const analysis = useMemo(() => {
    const parsed = ProblemFinderAnalysisResponseSchema.safeParse(analysisResult);
    return parsed.success ? parsed.data : null;
  }, [analysisResult]);
  const problem = analysis ? PROBLEMS[analysis.problemId] : null;

  // The sticky buy bar waits until the reader has scrolled past the named problem.
  const heroRef = useRef<HTMLDivElement>(null);
  const [isPastHero, setIsPastHero] = useState(false);
  useEffect(() => {
    const hero = heroRef.current;
    if (!hero || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(([entry]) => setIsPastHero(!entry.isIntersecting));
    observer.observe(hero);
    return () => observer.disconnect();
  }, [isComplete, analysis]);

  // Same event name as the other result pages so result-viewer counts stay comparable.
  useEffect(() => {
    if (!isComplete || !analysis || hasTrackedResultView.current) return;
    hasTrackedResultView.current = true;
    posthog?.capture("minitools_locked_section_viewed", {
      minitools_assessment_type: assessmentType,
      run_id: runId,
      problem_id: analysis.problemId,
    });
  }, [isComplete, analysis, assessmentType, runId, posthog]);

  if (!isComplete) {
    if (LoadingSpinner) return <LoadingSpinner />;
    return <WaitingForResult />;
  }

  if (!analysis || !problem) {
    return (
      <div className="max-w-xl mx-auto bg-white rounded-2xl shadow-sm ring-1 ring-black/[0.065] p-8 text-center">
        <h1 className="text-xl font-bold text-gray-900 mb-2">We could not read your result</h1>
        <p className="text-sm text-gray-600">
          Something went wrong while writing it. We have logged the problem. Email{" "}
          <a href="mailto:support@lifehack.org" className="text-emerald-700 underline">
            support@lifehack.org
          </a>{" "}
          and we will send your result by hand.
        </p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-32">
      <div ref={heroRef} className="max-w-2xl mx-auto px-6 py-10 md:py-12 text-white rounded-2xl" style={{ backgroundColor: BRAND }}>
        <div className="space-y-4">
          <p className="text-sm uppercase tracking-wide font-semibold text-amber-300">Your problem right now</p>
          <h1 className="text-3xl md:text-4xl font-bold leading-tight">{analysis.problemStatement}</h1>
          <p className="text-lg text-emerald-50 leading-relaxed">{analysis.costLine}</p>
          {email && (
            <p className="text-sm text-emerald-100">
              We&apos;re also emailing this to <span className="font-medium text-white">{email}</span>
            </p>
          )}
        </div>
      </div>

      <div className="max-w-2xl mx-auto pt-8 pb-6 space-y-6">
        {analysis.echoes.length > 0 && (
          <section aria-labelledby="echoes-heading" className="space-y-3">
            <h2 id="echoes-heading" className="text-sm uppercase tracking-wide text-gray-600 font-semibold">
              You told us
            </h2>
            <ul className="space-y-2">
              {analysis.echoes.map((echo) => (
                <li key={echo} className="bg-white rounded-xl px-4 py-3 text-base text-gray-900 ring-1 ring-black/[0.065]">
                  &ldquo;{echo}&rdquo;
                </li>
              ))}
            </ul>
          </section>
        )}

        <section className="bg-white rounded-2xl shadow-sm ring-1 ring-black/[0.065] p-6 space-y-3">
          <h2 className="text-xl font-bold text-gray-900">Why it keeps sticking</h2>
          <p className="text-base text-gray-700 leading-relaxed">{analysis.whyItSticks}</p>
        </section>

        <section className="rounded-2xl p-6 space-y-2 bg-amber-50 ring-1 ring-amber-200">
          <p className="text-sm uppercase tracking-wide font-semibold text-amber-900">Try this week</p>
          <h2 className="text-xl font-bold text-gray-900 leading-snug">{analysis.thisWeek.step}</h2>
          <p className="text-base text-gray-700">{analysis.thisWeek.detail}</p>
        </section>

        <p className="text-center text-base text-gray-700">
          That&apos;s step one. Your plan maps the next {PLAN_DAYS - 1}.
        </p>

        <PlanOffer
          runId={runId}
          email={email}
          problem={problem}
          analysis={analysis}
          ReportCheckoutButton={ReportCheckoutButton}
        />
      </div>

      {isPastHero && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg z-50">
          <div className="max-w-xl mx-auto px-4 py-3">
            <ReportCheckoutButton
              runId={runId}
              email={email}
              assessmentType="problem-finder"
              ctaLocation="sticky_bottom"
              className="w-full min-h-[48px] rounded-lg px-6 text-base font-bold text-white bg-[#2A6354] hover:bg-[#1f4a3f] transition-colors disabled:opacity-60"
            >
              {`Get My ${problem.planName} · $${PROBLEM_FINDER_PLAN_PRICE_USD}`}
            </ReportCheckoutButton>
            <p className="text-xs text-gray-600 text-center mt-2">30-day money-back guarantee</p>
          </div>
        </div>
      )}
    </div>
  );
}

export default ProblemFinderResultPage;
